import { FormSchema } from "@stormwater-software/lib-form";
import { UIControlType } from "@stormwater-software/lib-form-control-types";
import React, { MouseEventHandler, useContext, useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import ProjectContext from "../../../contexts/ProjectContext";
import ProjectPrintContext, {
  InspectionDateFilter,
} from "../../../contexts/ProjectPrintContext";
import LoadingModal from "../../Shared/Loading/LoadingModal";
import PrintModal from "../../Shared/Print/PrintModal";
import {
  getInspectionFilterSchema,
  InspectionSchemaOptions,
} from "./PrintInspectionsModal";
import AppContext from "../../../contexts/AppContext";
import tenantApi from "../../../utils/api/tenant";
import projectInspectionTemplatesApi from "../../../utils/api/projectInspectionTemplates";

export interface IPrintSwmpModal {
  onClose: MouseEventHandler<Element>;
}

function getProjectDoc(doc: any[]) {
  const options: { label: any; value: string; }[] = []
  const newDoc: string[] = []

  doc = doc.filter(e => e.name !== 'Fixed Details' && e.name !== "Fixed Details Project")
  const Doc = doc.map(ele => ele.name)

  Doc.map((newdoc) => {
    doc.map((ele) => {
      if (ele.name === newdoc) {
        newDoc.push(ele)
      }
    })
  })

  doc.map((newdoc) => {
    if (!Doc.includes(newdoc.name)) {
      newDoc.push(newdoc)
    }
  })

  newDoc.map((docName: any) => {

    if (docName.name !== "Active Site Maps") {
      switch (docName.name) {
        case "SWP Narrative/Maps":
          options.push({
            label: "SWP Narrative/Maps",
            value: "narrative"
          })
          break;

        case "Permits":
          options.push({
            label: "Permits",
            value: "permits"
          })
          break;
        case "Regulations":
          options.push({
            label: "Regulations",
            value: "regulations"
          })
          break;
        case "Construction Docs/Civil Drawings":
          options.push({
            label: "Construction Civil Docs",
            value: "constructionDocs"
          })
          break;
        case "Soil Reports":
          options.push({
            label: "Soil Reports",
            value: "soilReports"
          })
          break;
        case "Delegation Letters":
          options.push({
            label: "Delegation Letters",
            value: "delegationLetters"
          })
          break;
        case "Endangered Species":
          options.push({
            label: "Endangered Species",
            value: "endangeredSpecies"
          })
          break;
        case "Historical Properties":
          options.push({
            label: "Historical Properties",
            value: "historicalProperties"
          })
          break;
        case "Qualifications/Certifications":
          options.push({
            label: "Qualifications / Certifications",
            value: "qualifications"
          })
          break;
        case "Local Stormwater Regulations":
          options.push({
            label: "Local Stormwater Regulations",
            value: "localRegulations"
          })
          break;
        case "MISC":
          options.push({
            label: "MISC",
            value: "misc"
          })
          break;

        default:
          options.push({
            label: docName.name,
            value: docName.name
          })
      }
    }
  })

  return options
}

/**
 * Client-side filter: given all inspections and a selected template type id,
 * return only inspections whose template matches that type.
 * A specific form type must be selected - no "all" option.
 */
function filterInspectionsByFormType(
  allInspections: any[],
  selectedFormType: string | number | undefined,
): any[] {
  if (!selectedFormType) {
    return [];
  }

  const typeId = Number(selectedFormType);

  return allInspections.filter((insp: any) => {
    // The custom print inspections include inspection_template with inspection_type_id
    return insp.inspection_template?.inspection_type_id === typeId;
  });
}

const getPrintSchema = ({
  showInspectionSelection,
  doc,
  formTypeOptions,
  showFormTypeSelection,
  selectedFormType,
  ...schemaOptions
}: InspectionSchemaOptions & {
  showInspectionSelection: boolean;
  doc: any;
  formTypeOptions: { label: string; value: number | string }[];
  showFormTypeSelection: boolean;
  selectedFormType: string | number | undefined;
}) => {

  const schema: FormSchema = {
    appendix: {
      controlType: UIControlType.checkboxGroup,
      label: "Project Documents",
      options: getProjectDoc(doc),
      valueKey: "value",
      labelKey: "label",
      showSelectAll: true
    },
    projectDocs: {
      controlType: UIControlType.plainText,
      label: "Appendix"
    },
    bmpDetails: {
      controlType: UIControlType.checkbox,
      label: "BMP Details",
    },
    inspections: {
      controlType: UIControlType.checkbox,
      label: "Inspections",
    },
  };

  if (showInspectionSelection) {
    // Add form type selection when there are form types available
    if (showFormTypeSelection) {
      schema.inspectionFormType = {
        controlType: UIControlType.select,
        label: "Form Type*",
        options: formTypeOptions,
        validation: { required: true },
        placeholder: "Select a form type",
        style: { paddingLeft: "2rem" },
      };
    }

    // Only show inspection date filter and list when a form type is selected
    if (selectedFormType) {
      Object.assign(schema, getInspectionFilterSchema(schemaOptions));
    }
  }

  return schema;
};

const PrintSwmpModal: React.FC<IPrintSwmpModal> = ({ onClose }) => {
  const projectPrintContext = useContext(ProjectPrintContext);
  const projectContext = useContext(ProjectContext);
  const appStore = useContext(AppContext);
  const user = appStore.get("user");
  const router = useRouteMatch<{ id?: string }>()

  const [isAnnotationsLocked, setIsAnnotationsLocked] = React.useState<boolean>(false);
  const [formTypeOptions, setFormTypeOptions] = useState<{ label: string; value: number | string }[]>([]);

  const initialData = {
    bmpDetails: false,
    inspections: false,
    timeFilter: 3,
    inspectionFormType: undefined as string | number | undefined,
  };

  const setAnnotationLock = async () => {
    if (user && user.userTenantRoles && user.userTenantRoles.length > 0) {
      const isAppendOnly = await tenantApi.fetchIsAppendOnly(
        user.userTenantRoles[0].tenant_id,
        projectContext.project?.id,
      );
      
      setIsAnnotationsLocked(isAppendOnly);
    }
  }

  const loadFormTypes = async () => {
    const projectId = projectContext.project?.id;

    if (!projectId) return;

    try {
      const templateTypes = await projectInspectionTemplatesApi.getUniqueTemplateTypes(projectId);

      if (templateTypes && templateTypes.length > 0) {
        const options: { label: string; value: number | string }[] = templateTypes.map((t) => ({
          label: t.name,
          value: t.id,
        }));

        setFormTypeOptions(options);
      } else {
        setFormTypeOptions([]);
      }
    } catch {
      setFormTypeOptions([]);
    }
  };

  // const setAnnotationLock = async () => {
  //   if (user && user.userTenantRoles && user.userTenantRoles.length > 0) {
  //     const isAppendOnly = await tenantApi.fetchIsAppendOnly(
  //       user.userTenantRoles[0].tenant_id,
  //       projectContext.project?.id,
  //     );
  //     setIsAnnotationsLocked(isAppendOnly);
  //   }
  // }

  useEffect(() => {
    projectContext.inspectionsQuery.refetch();
    projectContext.getCustomPrint.refetch();
    projectContext.loadDocs(Number(router.params.id))
    setAnnotationLock();
    loadFormTypes();
  }, []);

  if (!projectContext.project || projectContext.inspectionsQuery.isFetching || !projectContext.projectDocs || projectContext.getCustomPrint.isFetching) {
    return <LoadingModal show />;
  }

  return (
    <PrintModal
      jobName={"swmp"}
      jobData={{
        projectId: projectContext.project.id,
      }}
      statusUpdateKey={projectContext.project.id}
      onClose={onClose}
      formSchema={(formData: Record<string, any>) => {
        const filter: InspectionDateFilter = {
          start: formData.start_date,
          end: formData.end_date,
          prevMonths: formData.timeFilter,
        };

        // Filter inspections client-side based on selected form type
        const allInspections = projectContext.getCustomPrint.data || [];
        const typeFilteredInspections = filterInspectionsByFormType(
          allInspections,
          formData.inspectionFormType,
        );

        const inspections = projectPrintContext.getInspectionOptions(
          typeFilteredInspections,
          filter,
        );

        const showFormTypeSelection = formTypeOptions.length > 0;
        const selectedFormType = formData.inspectionFormType;

        return getPrintSchema({
          inspections,
          filter,
          filterOptions: projectPrintContext.timeFilterOptions,
          showInspectionSelection: formData.inspections,
          inspectionTextLabel: selectedFormType
            ? projectPrintContext.getInspectionText({
                inspections,
                filter,
              })
            : "Select a form type to view inspections",
          doc: projectContext.projectDocs.documentTypes,
          formTypeOptions,
          showFormTypeSelection,
          selectedFormType,
        });
      }}
      initialFormData={{...initialData, isAnnotationsLocked: isAnnotationsLocked}}
      modalTitle="Print Project Documents"
      validateData={projectPrintContext.validateInspectionData}
    />
  );
};

export default PrintSwmpModal;
